import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Grid,
  Divider,
  InputAdornment,
  ThemeProvider,
  createTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import ShippingModal from './ShippingModal';
import { db } from '../config/firebase';
import { FEE_TYPES } from '../firebase/schema';
import { buildDefaultOrderFeeTypes, feeTypeTaxableByLabelMap, subscribeOrderFeeTypes } from '../firebase/orderFeeTypesSettings';

// Create a minimal theme for the modal that matches your site
const modalTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#98FB98'
    },
    background: {
      default: '#1C1C1C',
      paper: '#1C1C1C'
    },
    text: {
      primary: '#FFFFFF',
      secondary: 'rgba(255, 255, 255, 0.7)'
    }
  },
  typography: {
    fontFamily: '"Montserrat", sans-serif',
    allVariants: {
      textTransform: 'uppercase'
    }
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536
    }
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#363636',
            borderRadius: '0',
            '&:hover': {
              backgroundColor: '#404040'
            },
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '0'
            },
            '&:hover fieldset': {
              borderColor: '#98FB98'
            },
            '&.Mui-focused fieldset': {
              borderColor: '#98FB98',
              borderWidth: '1px'
            }
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
            '&.Mui-focused': {
              color: '#98FB98'
            }
          },
          '& input': {
            color: 'white',
            fontSize: '0.875rem',
            textTransform: 'uppercase',
            fontFamily: '"Montserrat", sans-serif',
            '&[type=number]': {
              '-moz-appearance': 'textfield',
              '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                '-webkit-appearance': 'none',
                margin: 0
              }
            }
          },
          '& textarea': {
            color: 'white',
            fontSize: '0.875rem',
            textTransform: 'uppercase',
            fontFamily: '"Montserrat", sans-serif'
          }
        }
      }
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#363636',
            borderRadius: '0',
            '&:hover': {
              backgroundColor: '#404040'
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '0'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#98FB98'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#98FB98',
              borderWidth: '1px'
            }
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)',
            '&.Mui-focused': {
              color: '#98FB98'
            }
          }
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: '#363636',
          borderRadius: '0',
          '&:hover': {
            backgroundColor: '#404040'
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '0'
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#98FB98'
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#98FB98',
            borderWidth: '1px'
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '0',
          textTransform: 'uppercase',
          fontWeight: 600,
          fontFamily: '"Montserrat", sans-serif'
        },
        contained: {
          backgroundColor: '#98FB98',
          color: '#000000',
          '&:hover': {
            backgroundColor: '#7FE87F'
          }
        },
        outlined: {
          borderColor: '#98FB98',
          color: '#98FB98',
          '&:hover': {
            borderColor: '#7FE87F',
            backgroundColor: 'rgba(152, 251, 152, 0.1)'
          }
        }
      }
    }
  }
});

// Styled native checkbox to match site styling
const NativeCheckbox = styled('input')`
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border: 2px solid var(--al-primary);
  background: transparent;
  cursor: pointer;
  position: relative;
  margin-right: 8px;
  flex-shrink: 0;
  
  &:checked {
    background: var(--al-primary);
    
    &:after {
      content: "×";
      color: var(--al-bg);
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      font-size: 14px;
      font-weight: bold;
      line-height: 1;
    }
  }

  &:hover {
    opacity: 0.8;
  }

  &:focus {
    outline: none;
    border-color: var(--al-primary);
  }
`;

const CheckboxLabel = styled('label')`
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 0.875rem;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  color: white;
  margin: 0;
  user-select: none;
  
  &:hover {
    opacity: 0.9;
  }
`;

const FeesModal = ({
  isOpen,
  onClose,
  onSave,
  initialData = {},
  shippingContext = {}
}) => {
  const providedGoodsRef = useRef(false);
  const sampleRef = useRef(false);
  const [feeTypeOptions, setFeeTypeOptions] = useState([]);
  const [fees, setFees] = useState([]);
  const [discount, setDiscount] = useState({ type: 'fixed', amount: '' });
  const [customerPoNumber, setCustomerPoNumber] = useState('');
  const [shippingDetails, setShippingDetails] = useState(null);
  const [showShippingModal, setShowShippingModal] = useState(false);
  const [additionalInstructions, setAdditionalInstructions] = useState({
    shipping: false,
    sorting: false,
    folding: false,
    bagging: false,
    fulfillment: false,
    neckTag: false,
    other: '',
    taxExempt: false,
    designWorkOnly: false,
    followUpEmail: false,
    doNotBill: false,
    instagramWorthy: false,
    providedGoods: false,
    sample: false
  });

  useEffect(() => {
    const unsub = subscribeOrderFeeTypes(
      db,
      (types) => setFeeTypeOptions(types),
      (err) => console.error('Order fee types subscription error:', err)
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    if (isOpen) {
      const merged = {
        shipping: false,
        sorting: false,
        folding: false,
        bagging: false,
        fulfillment: false,
        neckTag: false,
        other: '',
        taxExempt: false,
        designWorkOnly: false,
        followUpEmail: false,
        doNotBill: false,
        instagramWorthy: false,
        providedGoods: false,
        sample: false,
        ...(initialData.additionalInstructions || {})
      };
      providedGoodsRef.current = Boolean(merged.providedGoods);
      sampleRef.current = Boolean(merged.sample);
      setFees(initialData.fees || [
        { id: 1, type: '', quantity: '', price: '' },
        { id: 2, type: '', quantity: '', price: '' }
      ]);
      setDiscount(initialData.discount || { type: 'fixed', amount: '' });
      setCustomerPoNumber(initialData.customerPoNumber || '');
      setShippingDetails(initialData.shippingDetails || null);
      setAdditionalInstructions(merged);
    }
  }, [isOpen, initialData]);

  const feeTypesResolved = feeTypeOptions.length > 0 ? feeTypeOptions : buildDefaultOrderFeeTypes();

  const handleAddFee = () => {
    const newId = Math.max(...fees.map(f => f.id), 0) + 1;
    setFees([...fees, { id: newId, type: '', quantity: '', price: '' }]);
  };

  const handleRemoveFee = (id) => {
    setFees(fees.filter(fee => fee.id !== id));
  };

  const handleFeeChange = (id, field, value) => {
    setFees(fees.map(fee => 
      fee.id === id ? { ...fee, [field]: value } : fee
    ));
  };

  const handleInstructionChange = (field, value) => {
    if (field === 'providedGoods') providedGoodsRef.current = Boolean(value);
    if (field === 'sample') sampleRef.current = Boolean(value);
    setAdditionalInstructions(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    const taxByLabel = feeTypeTaxableByLabelMap(feeTypesResolved);
    const feesData = fees
      .filter((fee) => fee.type && fee.quantity && fee.price)
      .map((fee) => {
        const key = String(fee.type).trim().toLowerCase();
        const taxable = taxByLabel[key] !== false;
        return { ...fee, taxable };
      });
    const upchargeNum = Number(initialData.upchargePrice);
    const defaultUpcharge = (initialData.upchargePrice !== undefined && initialData.upchargePrice !== null && initialData.upchargePrice !== '' && !Number.isNaN(upchargeNum) && upchargeNum > 0)
      ? upchargeNum
      : 2;
    const providedGoods = providedGoodsRef.current || additionalInstructions.providedGoods;
    const sample = sampleRef.current || additionalInstructions.sample;
    onSave({
      fees: feesData,
      discount: discount.amount ? discount : { type: 'fixed', amount: '' },
      additionalInstructions: { ...additionalInstructions, providedGoods, sample },
      upchargePrice: providedGoods ? 0 : defaultUpcharge,
      sample,
      shippingDetails,
      customerPoNumber: customerPoNumber.trim()
    });
    onClose();
  };

  const handleShippingDataChange = (data) => {
    setShippingDetails(data);
  };

  const handleShipmentPurchased = (shipmentResult) => {
    const shippingAmount = Number(shipmentResult?.totalShipmentCost || 0);
    const normalizedPrice = Number.isFinite(shippingAmount) ? shippingAmount.toFixed(2) : '0.00';
    const shippingLabel = FEE_TYPES.SHIPPING;

    setShippingDetails(shipmentResult);
    setAdditionalInstructions((prev) => ({ ...prev, shipping: true }));

    const taxByLabel = feeTypeTaxableByLabelMap(feeTypesResolved);
    const shippingTaxable = taxByLabel[String(FEE_TYPES.SHIPPING).trim().toLowerCase()] !== false;

    setFees((prevFees) => {
      const existingIndex = prevFees.findIndex((fee) => fee.type === shippingLabel);
      if (existingIndex >= 0) {
        return prevFees.map((fee, index) => (
          index === existingIndex
            ? { ...fee, quantity: '1', price: normalizedPrice, taxable: shippingTaxable }
            : fee
        ));
      }
      const newId = Math.max(...prevFees.map((fee) => fee.id), 0) + 1;
      return [...prevFees, {
        id: newId,
        type: shippingLabel,
        quantity: '1',
        price: normalizedPrice,
        taxable: shippingTaxable
      }];
    });
  };

  const calculateFeeTotal = (fee) => {
    const quantity = parseFloat(fee.quantity) || 0;
    const price = parseFloat(fee.price) || 0;
    return (quantity * price).toFixed(2);
  };

  const calculateTotalFees = () => {
    return fees.reduce((total, fee) => {
      return total + (parseFloat(fee.quantity) || 0) * (parseFloat(fee.price) || 0);
    }, 0).toFixed(2);
  };

  return (
    <ThemeProvider theme={modalTheme}>
      <Dialog 
        open={isOpen} 
        onClose={onClose}
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'var(--al-bg)',
            color: 'white',
            borderRadius: 0,
            maxWidth: '800px',
            width: '90vw'
          }
        }}
      >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 2
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
          Edit Fees & Instructions
        </Typography>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Fees Section */}
        <Box sx={{ mb: 4 }}>

          {fees.map((fee, index) => (
            <Box key={fee.id} sx={{ 
              mb: 2, 
              p: 2, 
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.02)',
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              <IconButton 
                onClick={() => handleRemoveFee(fee.id)}
                sx={{ color: '#ff4444', flexShrink: 0 }}
                size="small"
              >
                <DeleteIcon />
              </IconButton>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Fee Type</InputLabel>
                    <Select
                      value={fee.type}
                      onChange={(e) => handleFeeChange(fee.id, 'type', e.target.value)}
                      label="Fee Type"
                    >
                      <MenuItem value="">
                        <em>Select Type</em>
                      </MenuItem>
                      {feeTypesResolved.map((t) => (
                        <MenuItem key={t.label} value={t.label}>{t.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Quantity"
                      type="number"
                      value={fee.quantity}
                      onChange={(e) => handleFeeChange(fee.id, 'quantity', e.target.value)}
                    />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Price"
                    type="number"
                    value={fee.price}
                    onChange={(e) => handleFeeChange(fee.id, 'price', e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          ))}


          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddFee}
              >
                Add Fee
              </Button>
              <Button
                variant="contained"
                onClick={() => setShowShippingModal(true)}
              >
                Shipping Label
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Discount Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'rgba(255, 255, 255, 0.9)' }}>
            Discount
          </Typography>
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}>
            <FormControl size="small" sx={{ minWidth: 70 }}>
              <Select
                value={discount.type}
                onChange={(e) => setDiscount({ ...discount, type: e.target.value })}
                sx={{ '& .MuiSelect-select': { py: 1 } }}
              >
                <MenuItem value="fixed">$</MenuItem>
                <MenuItem value="percent">%</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="number"
              placeholder="0"
              value={discount.amount}
              onChange={(e) => setDiscount({ ...discount, amount: e.target.value })}
              sx={{ width: 120 }}
            />
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontStyle: 'italic', ml: 2 }}>
              {discount.type === 'fixed' 
                ? 'Subtracts fixed amount before tax'
                : 'Reduces subtotal by percentage before tax'
              }
            </Typography>
          </Box>
        </Box>

        {/* Additional Instructions Section */}
        <Box>

          <Grid container spacing={2}>
            {/* Service Instructions */}
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.8)' }}>
                Services
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <CheckboxLabel>
                  <NativeCheckbox
                    type="checkbox"
                    checked={additionalInstructions.shipping}
                    onChange={(e) => handleInstructionChange('shipping', e.target.checked)}
                  />
                  Shipping
                </CheckboxLabel>
                <CheckboxLabel>
                  <NativeCheckbox
                    type="checkbox"
                    checked={additionalInstructions.sorting}
                    onChange={(e) => handleInstructionChange('sorting', e.target.checked)}
                  />
                  Sorting
                </CheckboxLabel>
                <CheckboxLabel>
                  <NativeCheckbox
                    type="checkbox"
                    checked={additionalInstructions.folding}
                    onChange={(e) => handleInstructionChange('folding', e.target.checked)}
                  />
                  Folding
                </CheckboxLabel>
                <CheckboxLabel>
                  <NativeCheckbox
                    type="checkbox"
                    checked={additionalInstructions.bagging}
                    onChange={(e) => handleInstructionChange('bagging', e.target.checked)}
                  />
                  Bagging
                </CheckboxLabel>
                <CheckboxLabel>
                  <NativeCheckbox
                    type="checkbox"
                    checked={additionalInstructions.fulfillment}
                    onChange={(e) => handleInstructionChange('fulfillment', e.target.checked)}
                  />
                  Fulfillment
                </CheckboxLabel>
                <CheckboxLabel>
                  <NativeCheckbox
                    type="checkbox"
                    checked={additionalInstructions.neckTag}
                    onChange={(e) => handleInstructionChange('neckTag', e.target.checked)}
                  />
                  Neck Tag
                </CheckboxLabel>
              </Box>
            </Grid>

            {/* Order Options */}
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.8)' }}>
                Order Options
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <CheckboxLabel>
                  <NativeCheckbox
                    type="checkbox"
                    checked={additionalInstructions.taxExempt}
                    onChange={(e) => handleInstructionChange('taxExempt', e.target.checked)}
                  />
                  Tax Exempt
                </CheckboxLabel>
                <CheckboxLabel>
                  <NativeCheckbox
                    type="checkbox"
                    checked={additionalInstructions.designWorkOnly}
                    onChange={(e) => handleInstructionChange('designWorkOnly', e.target.checked)}
                  />
                  Design Work Only
                </CheckboxLabel>
                <CheckboxLabel>
                  <NativeCheckbox
                    type="checkbox"
                    checked={additionalInstructions.followUpEmail}
                    onChange={(e) => handleInstructionChange('followUpEmail', e.target.checked)}
                  />
                  Follow-Up Email
                </CheckboxLabel>
                <CheckboxLabel>
                  <NativeCheckbox
                    type="checkbox"
                    checked={additionalInstructions.doNotBill}
                    onChange={(e) => handleInstructionChange('doNotBill', e.target.checked)}
                  />
                  Do NOT Bill
                </CheckboxLabel>
                <CheckboxLabel>
                  <NativeCheckbox
                    type="checkbox"
                    checked={additionalInstructions.instagramWorthy}
                    onChange={(e) => handleInstructionChange('instagramWorthy', e.target.checked)}
                  />
                  Instagram Worthy
                </CheckboxLabel>
                <CheckboxLabel>
                  <NativeCheckbox
                    type="checkbox"
                    checked={additionalInstructions.providedGoods}
                    onChange={(e) => handleInstructionChange('providedGoods', e.target.checked)}
                  />
                  Provided Goods (sets 2XL upcharge to $0)
                </CheckboxLabel>
                <CheckboxLabel>
                  <NativeCheckbox
                    type="checkbox"
                    checked={additionalInstructions.sample}
                    onChange={(e) => handleInstructionChange('sample', e.target.checked)}
                  />
                  Sample (blank garment only — no screen cut or print)
                </CheckboxLabel>
              </Box>
            </Grid>
          </Grid>

          {/* Customer PO (optional — used on the invoice for corporate buyers) */}
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="PO Number"
              placeholder="Optional — buyer's PO for their accounting"
              value={customerPoNumber}
              onChange={(e) => setCustomerPoNumber(e.target.value)}
              inputProps={{ maxLength: 64 }}
            />
          </Box>

          {/* Other Instructions Text Field */}
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Other Instructions"
              value={additionalInstructions.other}
              onChange={(e) => handleInstructionChange('other', e.target.value)}
              placeholder="Enter any additional instructions or notes..."
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained">
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
    <ShippingModal
      isOpen={showShippingModal}
      onClose={() => setShowShippingModal(false)}
      onShippingDataChange={handleShippingDataChange}
      onShipmentPurchased={handleShipmentPurchased}
      shippingContext={shippingContext}
      initialShippingData={shippingDetails}
    />
    </ThemeProvider>
  );
};

export default FeesModal;
